import { useState } from 'react';
import { Alert, Button, Grid, NumberInput, Select, Table, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconTrash } from '@tabler/icons-react';
import { MOVEMENT_SOURCES, type MovementSource } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { parseNum } from '../../../lib/parseNum';
import { useActiveProducts, useAddRunInput, useAllProducts, useDeleteRunInput, useLocations } from '../api';
import { formatLineQty, formatPct, qtyInputLabel } from '../format';
import type { RunInputLineDto } from '../types';

/** Inputs step of "Record a shift" / a kiln batch (BUILD_SPEC.md §8.3: "inputs
 * (product, from-location, tonnes, moisture, source)"). Editable while the run is
 * OPEN or CLOSED (services/api/src/modules/production/lib.ts's
 * `assertLinesEditable`) — corrections are add/remove, not inline edit, since each
 * line is a real persisted row the moment it's added. */
export function RunInputsEditor({
  runId,
  siteId,
  lines,
  editable,
}: {
  runId: string;
  siteId: string;
  lines: RunInputLineDto[];
  editable: boolean;
}) {
  const locationsQuery = useLocations(siteId);
  // Picker offers ACTIVE products only; the lookup map covers ALL products so an
  // already-recorded line for a since-deactivated product still resolves its
  // name/baseUom (matching the server's un-filtered products join — see
  // api.ts's useAllProducts doc).
  const productsQuery = useActiveProducts();
  const allProductsQuery = useAllProducts();
  const addInput = useAddRunInput(runId);
  const deleteInput = useDeleteRunInput(runId);

  const [productId, setProductId] = useState<string | null>(null);
  const [fromLocationId, setFromLocationId] = useState<string | null>(null);
  const [qtyKg, setQtyKg] = useState<number | string>('');
  const [mcPct, setMcPct] = useState<number | string>('');
  const [source, setSource] = useState<MovementSource>('MEASURED');
  const [handlingUnits, setHandlingUnits] = useState<number | string>('');

  const locations = locationsQuery.data?.items ?? [];
  const products = productsQuery.data?.items ?? [];
  const locationsById = new Map(locations.map((l) => [l.id, l]));
  const productsById = new Map((allProductsQuery.data?.items ?? []).map((p) => [p.id, p]));
  // Drives the entry form's quantity label (below) — the SAME baseUom lookup the
  // table rows use for their per-line unit display.
  const selectedProduct = productId ? productsById.get(productId) : undefined;

  const parsedQtyKg = parseNum(qtyKg);
  const parsedMcPct = parseNum(mcPct);
  const parsedHandlingUnits = parseNum(handlingUnits);

  function reset() {
    setProductId(null);
    setFromLocationId(null);
    setQtyKg('');
    setMcPct('');
    setHandlingUnits('');
    addInput.reset();
  }

  function handleAdd() {
    if (!productId || !fromLocationId || parsedQtyKg === null) return;
    addInput.mutate(
      {
        productId,
        fromLocationId,
        qtyKg: parsedQtyKg.toFixed(3),
        mcPct: parsedMcPct === null ? null : parsedMcPct.toFixed(2),
        source,
        handlingUnits: parsedHandlingUnits,
      },
      { onSuccess: reset },
    );
  }

  return (
    <>
      <Title order={4} mb="xs">
        Inputs
      </Title>
      {editable && (
        <Grid align="flex-end" mb="sm">
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <Select
              label="Product"
              placeholder="Product"
              data={products.map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }))}
              value={productId}
              onChange={setProductId}
              searchable
              disabled={productsQuery.isLoading}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <Select
              label="From location"
              placeholder="Location"
              data={locations.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
              value={fromLocationId}
              onChange={setFromLocationId}
              searchable
              disabled={locationsQuery.isLoading}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, sm: 2 }}>
            <NumberInput
              label={qtyInputLabel(selectedProduct?.baseUom, 'Tonnes (kg)', 'Qty (units)')}
              decimalScale={3}
              min={0}
              value={qtyKg}
              onChange={setQtyKg}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, sm: 2 }}>
            <NumberInput
              label="Moisture %"
              decimalScale={2}
              min={0}
              max={100}
              value={mcPct}
              onChange={setMcPct}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, sm: 1 }}>
            <NumberInput
              label="Cages"
              decimalScale={0}
              min={0}
              value={handlingUnits}
              onChange={setHandlingUnits}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, sm: 1 }}>
            <Select
              label="Source"
              data={MOVEMENT_SOURCES.map((s) => ({ value: s, label: s }))}
              value={source}
              onChange={(v) => setSource((v as MovementSource) ?? 'MEASURED')}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Button
              size="xs"
              variant="light"
              disabled={!productId || !fromLocationId || parsedQtyKg === null}
              loading={addInput.isPending}
              onClick={handleAdd}
            >
              Add input
            </Button>
          </Grid.Col>
        </Grid>
      )}
      {addInput.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} mb="sm">
          {addInput.error instanceof ApiRequestError ? addInput.error.message : 'Failed to add input'}
        </Alert>
      )}
      <Table.ScrollContainer minWidth={600}>
        <Table verticalSpacing="xs" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Product</Table.Th>
              <Table.Th>From</Table.Th>
              {/* Neutral header — a run can mix KG and EACH products, so the unit
                  is shown per-row (formatLineQty) instead of a single "(kg)"
                  header that would lie for an EACH line. */}
              <Table.Th>Qty</Table.Th>
              <Table.Th>MC%</Table.Th>
              <Table.Th>Cages</Table.Th>
              <Table.Th>Source</Table.Th>
              {editable && <Table.Th />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {lines.map((line) => (
              <Table.Tr key={line.id}>
                <Table.Td>{productsById.get(line.productId)?.name ?? line.productId}</Table.Td>
                <Table.Td>{locationsById.get(line.fromLocationId)?.name ?? line.fromLocationId}</Table.Td>
                <Table.Td>{formatLineQty(line.qtyKg, productsById.get(line.productId)?.baseUom)}</Table.Td>
                <Table.Td>{formatPct(line.mcPct)}</Table.Td>
                <Table.Td>{line.handlingUnits ?? '—'}</Table.Td>
                <Table.Td>{line.source}</Table.Td>
                {editable && (
                  <Table.Td>
                    <Button variant="subtle" color="red" size="compact-sm" onClick={() => deleteInput.mutate(line.id)}>
                      <IconTrash size={14} />
                    </Button>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {lines.length === 0 && (
          <Text c="dimmed" size="sm" mt="xs">
            No inputs recorded yet.
          </Text>
        )}
      </Table.ScrollContainer>
    </>
  );
}
